import React, { useState, useContext } from "react";
import qapi from "views_shared/app/javascript/api";
import { companyUrl, toAbsoluteUrl } from "views_shared/app/javascript/utils/urls";
import { throwOnApiError } from "views_shared/app/javascript/api/api";
import EntityHeader from "../../../profiles/EntityHeader";
import FollowControl from "../../../profiles/FollowControl";
import ShareProfileButton from "../../../profiles/ShareProfileButton";
import { CompanyContext } from "../../data/company_context_management";
import ConfirmModal from "../../../confirm_modal";
import LoadingImage from "../../../loading_image";
import AddSectionButton from "./add_sections_button";
import ProfileImage, { fileToBase64WithName, hasAcceptedFileType } from "../../../ProfileImage";

const companyProfileBannerImage = RAILS_ASSET_URL("/company-profile-banner.png");

function CompanyHeader() {
  const { company, viewerContext, setOpenEditModal, setDoTopLevelFetch, reloadCompany } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;
  const [savingBannerImage, setSavingBannerImage] = useState(false);
  const [removingBannerImage, setRemovingBannerImage] = useState(false);
  const [showDeleteBannerModal, setDeleteBannerModal] = useState(false);
  const [failedBannerSrc, setFailedBannerSrc] = useState(null);

  const showEditModal = () => {
    setOpenEditModal("HEADER");
  };

  const renderVisibilityBadge = () => {
    // The badge reports the company's current visibility. It edits nothing, so it follows the
    // owner, not the write switch.
    if (!viewerContext?.isSelf) return null;

    let colorClass;
    let badgeText;

    if (company.visibility === "hidden") {
      colorClass = "danger";
      badgeText = "Hidden";
    }
    if (company.visibility === "network") {
      colorClass = "info";
      badgeText = "Network Only";
    }
    if (company.visibility === "public") {
      colorClass = "success";
      badgeText = "Public";
    }

    if (colorClass && badgeText) {
      return <div className={`badge bg-${colorClass}`}>Visibility: {badgeText}</div>;
    }

    return null;
  };

  const onBannerChange = async (evt) => {
    const inputEl = evt.target;
    const { files = [] } = inputEl;
    if (files.length === 0) return;

    const file = files[0];

    if (!hasAcceptedFileType(file)) {
      // eslint-disable-next-line no-alert
      alert("File not supported. Please upload a PNG, JPG, JPEG, or SVG file.");
      inputEl.value = ""; // allow re-selecting the same (rejected) file
      return;
    }

    setSavingBannerImage(true);

    try {
      const { base64, filename } = await fileToBase64WithName(file);
      const attributes = {
        image_cloudinary: base64,
        original_filename: filename,
      };

      // No banner yet means this is a create; an existing one means update.
      if (company.banner_image_url) {
        throwOnApiError(await qapi.companies.updateBannerImageById(company.id, attributes));
      } else {
        throwOnApiError(await qapi.companies.createBannerImageById(company.id, attributes));
      }

      setDoTopLevelFetch(true);
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert("Something went wrong, please try again later");
    } finally {
      setSavingBannerImage(false);
    }
  };

  const removeBannerImage = async () => {
    setRemovingBannerImage(true);

    try {
      throwOnApiError(await qapi.companies.deleteBannerImageById(company.id));
      setDoTopLevelFetch(true);
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert("Something went wrong, please try again.");
    } finally {
      setRemovingBannerImage(false);
    }
  };

  const renderDeleteBannerModal = () => (
    <ConfirmModal
      confirmYesText="Yes"
      confirmNoText="No"
      confirmContent="Are you sure you want to remove this banner image?"
      confirmYes={removeBannerImage}
      confirmNo={() => setDeleteBannerModal(false)}
      isOpening
      className="modal-sm p-3 confirm-remove-banner-image-modal"
      confirmNoButtonClassName="shadow-none"
      isSubmitting={removingBannerImage}
    />
  );

  const renderDeleteBannerButton = () => {
    if (!editable || !company.banner_image_url) return null;

    if (removingBannerImage) {
      return (
        <button className="btn banner-image-control btn-remove-banner-image me-2" type="button">
          <LoadingImage className="" height="24" />
        </button>
      );
    }
    return (
      <button
        className="btn banner-image-control btn-remove-banner-image me-2"
        type="button"
        aria-label="Remove cover photo"
        onClick={() => setDeleteBannerModal(true)}
      >
        <i className="fa-solid fa-trash-alt" />
      </button>
    );
  };

  const renderEditBannerButton = () => {
    if (!editable) return null;

    if (savingBannerImage) {
      return <LoadingImage className="me-2" height="24" />;
    }

    return (
      <div className="d-inline-block border-0 p-0 bg-transparent me-2">
        <label className="m-0 cursor-pointer btn banner-image-control form-label">
          <input
            type="file"
            accept=".png, .jpg, .jpeg, .svg"
            className=""
            onChange={onBannerChange}
            style={{ display: "none" }}
            id="banner_image_input"
          />
          <span className="d-none d-sm-inline">
            {company.banner_image_url ? "Change Cover Photo" : "Upload Cover Photo"} (1500x300px)
          </span>
          <span className="d-inline d-sm-none">
            <i className="fa-solid fa-pencil-alt" />
          </span>
        </label>
      </div>
    );
  };

  // Social links (Twitter/LinkedIn/Facebook) and the Message action moved out of the header into the
  // rail's Connect card (see CompanyShow) — the same relocation Product and PR Profile already made,
  // so contact/social affordances live in one place instead of being split across the header and the
  // rail. Only the website link stays here, per the ruled actions-row parity with the other entity
  // pages: [View Website] [Share Profile].
  const urlContactInfo = company.contact_details?.find((detail) => detail.type === "url") || {};
  const websiteUrl = urlContactInfo.value;
  const canonicalUrl = toAbsoluteUrl(companyUrl({ slug: company.slug, migrate_id: company.migrate_id }));

  // Company is the only entity engine with an upload/delete feature, so that write logic stays
  // engine-side here; the shell provides only the fixed-height band + slots. Every viewer now sees
  // a cover (the shared `.person-header-banner` chassis, same as every other profile page): the
  // uploaded image when there is one, the generated default asset otherwise. The generated default
  // is never itself deletable, which is why `renderDeleteBannerButton` still gates on
  // `company.banner_image_url` rather than on `editable` alone.
  //
  // A React onError handler isn't a native DOM listener, so nulling `img.onerror` can't detach
  // it -- the failed src is tracked in state instead. The state records the uploaded banner URL
  // itself (not whatever is currently on screen), so once it has failed once the bundled default
  // stays showing even if that default also errors: the handler keeps recording the same
  // `company.banner_image_url` value, which is a no-op re-render rather than a loop or a flip
  // back to the image that just failed.
  const bannerFailed = Boolean(company.banner_image_url) && failedBannerSrc === company.banner_image_url;
  const bannerSrc = company.banner_image_url && !bannerFailed ? company.banner_image_url : companyProfileBannerImage;
  const banner = (
    <div className="person-header-banner position-relative">
      <img alt="" src={bannerSrc} onError={() => setFailedBannerSrc(company.banner_image_url)} />
      {editable && (
        <div
          className="position-absolute"
          style={{
            bottom: "10px",
            right: "0",
          }}
        >
          {renderDeleteBannerButton()}
          {renderEditBannerButton()}
        </div>
      )}
    </div>
  );

  const avatar = (
    <ProfileImage
      editable={editable}
      imgOwnerId={company.id}
      profileImageUrl={company.profile_image_url}
      imgOwnerName={company.name}
      imgOwnerType="company"
      // The awaitable read, not the doTopLevelFetch flag: ProfileImage keeps its controls disabled
      // until this resolves, and a flag setter resolves before the company has been re-read.
      onChange={reloadCompany}
      framed
    />
  );

  const identity = (
    <>
      {/* Owner controls live on the name line, not in the actions row: the pen is the same header
          edit affordance every person engine uses, and Add-a-section sits beside it so the pinned
          row stays purely audience-facing — an owner button styled like the public pair read as
          broken rather than privileged. */}
      <div className="d-flex flex-wrap align-items-center mt-2">
        <h1 className="fw-bold p-0 m-0">{company.name}</h1>
        {editable && (
          <>
            <button
              type="button"
              aria-label="Edit Header"
              className="btn btn-primary btn-sm ms-2 d-inline-flex align-items-center justify-content-center"
              style={{ width: "2rem", height: "2rem", padding: 0 }}
              onClick={showEditModal}
            >
              <i className="fa-solid fa-pencil-alt" />
            </button>
            <AddSectionButton />
          </>
        )}
      </div>

      <div className="row ms-0 mt-3 pe-3">{company.headline}</div>

      {company.headline && <div className="mb-3" />}
    </>
  );

  // One actions row, pinned top-right by the shared chassis, purely audience-facing: Follow
  // (viewers only — you don't follow your own page), [View Website] (render-when-present) and
  // [Share Profile] via the shared buttons. Owner controls live on the identity's name line
  // instead. No kebab on entity pages.
  const actions = (
    // flex-wrap: this row's own display:flex (see companies.scss .company-header-btns-container)
    // doesn't wrap by default; the controls it holds need to be able to stack rather than overflow
    // a phone's content width. The row's items already carry me-2/mb-2 for spacing, so no gap
    // utility is needed here.
    <div className="align-items-center justify-content-sm-start pb-3 ms-0 flex-wrap company-header-btns-container">
      {!viewerContext?.isSelf && <FollowControl followableType="Id::PromoEntity::Company" followableId={company.id} />}

      {websiteUrl && (
        <a
          href={websiteUrl}
          className="btn btn-outline-primary text-nowrap d-block mb-2 me-2"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="fa-solid fa-globe me-2" />
          View Website
        </a>
      )}

      <ShareProfileButton url={canonicalUrl} />
    </div>
  );

  // Badges stack in the utility rail (level with the title, per EntityHeader's own column layout) —
  // no float needed to separate them from anything else now that they're the aside's only content.
  const aside = (
    <div className="d-flex flex-column justify-content-start">
      {company.displayType === "pr_firm" && <span className="badge text-bg-light mb-2">PR Agency</span>}
      {renderVisibilityBadge()}
    </div>
  );

  return (
    <>
      {showDeleteBannerModal && renderDeleteBannerModal()}
      <EntityHeader
        banner={banner}
        avatar={avatar}
        avatarWrapperClassName="company-logo-container"
        identity={identity}
        actions={actions}
        aside={aside}
      />
    </>
  );
}

export default CompanyHeader;
